import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Product } from 'src/components/restructure/product/types/product'
import { Picture } from 'src/components/ui/Image'
import { clientStore } from 'src/services/axiosInstance'
import Skeleton from 'react-loading-skeleton'
import { useMobile } from 'src/hooks/useMobile'
import { InView } from 'react-intersection-observer'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude/dispatchAmplitudeEvent'
import { PLP_COLLECTION_COMPONENTS_EXPERIMENT_ID } from 'src/components/common/StatsigExposure/StatsigExposure'
import { Statsig } from 'src/sdk/statsig'
import ProductCard from 'src/components/restructure/product/ProductCard'
import ArrowLeftClub from 'src/components/Icons/ArrowLeftClub'

interface InspiringShowcaseProps {
  enableShowcase: boolean
  backgroundImage: {
    imageDesktop: string
    imageMobile: string
  }
  title: {
    text: string
    textColor: string
  }
  products: string[] | Record<string, string>
  showOnMobile: boolean
  showOnDesktop: boolean
  ignoreStatsigGate?: boolean
}

export function InspiringShowcase({
  products: productIds,
  backgroundImage,
  showOnMobile,
  showOnDesktop,
  enableShowcase,
  title,
  ignoreStatsigGate = false,
}: InspiringShowcaseProps) {
  const statsigGateValue = Statsig.useStatsigFeatureGateValue(
    PLP_COLLECTION_COMPONENTS_EXPERIMENT_ID
  )

  const isCollectionComponentsEnabled = ignoreStatsigGate || statsigGateValue

  const [products, setProducts] = useState<Product[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [hasSliderOverflow, setHasSliderOverflow] = useState(false)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)
  const [thumbWidthPercent, setThumbWidthPercent] = useState(100)
  const [thumbOffsetPercent, setThumbOffsetPercent] = useState(0)
  const sliderRef = useRef<HTMLDivElement>(null)

  const { isMobile } = useMobile()
  const { imageDesktop, imageMobile } = backgroundImage
  const normalizedProductIds = useMemo(() => {
    const rawIds = Array.isArray(productIds)
      ? productIds
      : Object.values(productIds ?? {})

    return rawIds
      .map((id) => String(id ?? '').trim())
      .filter(Boolean)
      .slice(0, 8)
  }, [productIds])

  const hasMinimumProducts = normalizedProductIds.length >= 3

  const updateSliderState = useCallback(() => {
    if (!sliderRef.current) {
      setHasSliderOverflow(false)
      setCanScrollPrev(false)
      setCanScrollNext(false)
      setThumbWidthPercent(100)
      setThumbOffsetPercent(0)

      return
    }

    const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current
    const maxScrollLeft = scrollWidth - clientWidth
    const hasOverflow = maxScrollLeft > 2

    setHasSliderOverflow(hasOverflow)

    if (!hasOverflow) {
      setCanScrollPrev(false)
      setCanScrollNext(false)
      setThumbWidthPercent(100)
      setThumbOffsetPercent(0)

      return
    }

    setCanScrollPrev(scrollLeft > 2)
    setCanScrollNext(scrollLeft < maxScrollLeft - 2)

    const rawThumbWidth = (clientWidth / scrollWidth) * 100
    const nextThumbWidth = Math.max(rawThumbWidth, 12)
    const normalizedProgress =
      maxScrollLeft > 0
        ? Math.min(Math.max(scrollLeft / maxScrollLeft, 0), 1)
        : 0

    setThumbWidthPercent(nextThumbWidth)
    setThumbOffsetPercent(normalizedProgress * (100 - nextThumbWidth))
  }, [])

  useEffect(() => {
    if (!hasMinimumProducts) {
      setProducts([])
      setLoading(false)

      return
    }

    if (products?.length) {
      return
    }

    setLoading(true)

    Promise.all(
      normalizedProductIds.map((id) =>
        clientStore
          .get<Product>(`/v2/api/decathlon-pdp/product?productId=${id}`)
          .then((response) => response.data)
          .catch((error) => {
            console.error(`Erro ao buscar produto ${id}:`, error)

            return null
          })
      )
    ).then((results) => {
      setProducts(results.filter(Boolean) as Product[])
      setLoading(false)
    })
  }, [hasMinimumProducts, normalizedProductIds, products?.length])

  useEffect(() => {
    updateSliderState()

    const onResize = () => updateSliderState()

    window.addEventListener('resize', onResize)

    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [products?.length, updateSliderState])

  const handleHorizontalScroll = (direction: 'previous' | 'next') => {
    if (
      !sliderRef.current ||
      (direction === 'previous' && !canScrollPrev) ||
      (direction === 'next' && !canScrollNext)
    ) {
      return
    }

    const scrollDistance = 326

    sliderRef.current.scrollBy({
      left: direction === 'next' ? scrollDistance : -scrollDistance,
      behavior: 'smooth',
    })
  }

  if (loading) {
    return (
      <Skeleton
        className="mb-10 md:mb-16 mt-5 md:mt-[54px]"
        height={isMobile ? 798 : 656}
      />
    )
  }

  if (
    !products?.length ||
    !hasMinimumProducts ||
    !isCollectionComponentsEnabled ||
    (isMobile && !showOnMobile) ||
    (!isMobile && !showOnDesktop) ||
    !enableShowcase
  ) {
    return null
  }

  const shouldShowSliderControls =
    hasSliderOverflow && (products?.length ?? 0) > 3

  return (
    <InView
      as="section"
      triggerOnce
      className="mb-10 md:mb-16 mt-5 md:mt-[54px]"
      onChange={(inView) => {
        if (inView) {
          dispatchAmplitudeEvent({
            eventName: 'Promo Feature Viewed',
            eventData: {
              type: 'Contextual',
              'promotional element name': 'Vitrine Inspiracional',
            },
          })
        }
      }}
    >
      <div className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen overflow-hidden bg-[#0A0A0A]">
        <div className="relative h-[761px] w-full md:h-[656px]">
          <Picture
            className="absolute inset-0 h-[488px] w-full object-cover md:h-full"
            sources={[
              {
                src: imageMobile ?? '',
                width: 780,
                height: 976,
                media: '(max-width: 767px)',
                options: {
                  fitIn: true,
                  filters: {
                    quality: '95',
                    format: 'webp',
                  },
                },
              },
              {
                src: imageDesktop ?? '',
                width: 3840,
                height: 1312,
                media: '(min-width: 768px)',
                options: {
                  fitIn: true,
                  filters: {
                    quality: '95',
                    format: 'webp',
                  },
                },
              },
            ]}
            img={{
              src: imageDesktop ?? imageMobile ?? '',
              width: 1920,
              height: 656,
              alt: 'vitrine inspiracional',
              loading: 'eager',
              fetchpriority: 'high',
            }}
            shouldCompress
          />

          <div className="absolute bottom-0 md:top-0 right-0 w-full h-[75%] md:w-[65%] md:h-full bg-gradient-to-t md:bg-gradient-to-l from-black/95 via-black/95  md:via-black/90 to-transparent" />

          <div className="relative z-20 flex h-full items-end pb-4 md:pb-[88px]">
            <div className="w-full pl-[17px] md:px-8 lg:px-10">
              <div className=" mx-auto grid w-full max-w-[1640px] grid-cols-1 gap-6 md:grid-cols-[minmax(240px,376px)_minmax(0,950px)] md:items-end md:gap-[clamp(32px,13vw,230px)]">
                {title?.text ? (
                  <p
                    className="font-inter m-0 max-w-[267px] md:max-w-[390px] text-[28px] font-semibold leading-[29px] tracking-[-0.56px] md:text-[32px] md:font-bold md:leading-[30px] lg:text-[40px] lg:leading-[32px]"
                    style={{ color: title?.textColor || '#FFFFFF' }}
                  >
                    {title.text}
                  </p>
                ) : null}

                <div className="relative w-full md:mb-8 md:max-w-[950px]">
                  {shouldShowSliderControls ? (
                    <>
                      <button
                        type="button"
                        className={`hidden 2xl:flex absolute left-[-54px] top-1/2 -translate-y-1/2 z-20 w-10 h-10 p-0 rounded-full border border-[#e4e4e4] bg-white items-center justify-center transition-colors hover:border-[#cfcfcf] disabled:cursor-default disabled:border-[#e4e4e4] disabled:hover:border-[#e4e4e4] ${
                          canScrollPrev ? 'text-[#222]' : 'text-[#656565]'
                        }`}
                        onClick={() => handleHorizontalScroll('previous')}
                        aria-label="Ver cards anteriores"
                        disabled={!canScrollPrev}
                      >
                        <span
                          className="inline-flex items-center justify-center w-4 h-4 leading-none"
                          aria-hidden="true"
                        >
                          <ArrowLeftClub
                            color={canScrollPrev ? '#222' : '#656565'}
                          />
                        </span>
                      </button>

                      <button
                        type="button"
                        className={`hidden 2xl:flex absolute right-[-44px] top-1/2 -translate-y-1/2 z-20 w-10 h-10 p-0 rounded-full border border-[#e4e4e4] bg-white items-center justify-center transition-colors hover:border-[#cfcfcf] disabled:cursor-default disabled:border-[#e4e4e4] disabled:hover:border-[#e4e4e4] ${
                          canScrollNext ? 'text-[#222]' : 'text-[#656565]'
                        }`}
                        onClick={() => handleHorizontalScroll('next')}
                        aria-label="Ver próximos cards"
                        disabled={!canScrollNext}
                      >
                        <span
                          className="inline-flex items-center justify-center w-4 h-4 leading-none rotate-180"
                          aria-hidden="true"
                        >
                          <ArrowLeftClub
                            color={canScrollNext ? '#222' : '#656565'}
                          />
                        </span>
                      </button>
                    </>
                  ) : null}

                  <div
                    ref={sliderRef}
                    onScroll={updateSliderState}
                    className="w-full overflow-x-auto no-scrollbar snap-x snap-mandatory scroll-px-0"
                  >
                    <div className="flex w-max gap-[14px] md:gap-[15px]">
                      {products.map((product, index) => (
                        <div
                          key={product.productId}
                          className="max-h-[380px] md:max-h-[460px] w-[196px] shrink-0 snap-start bg-white pt-[11.5px] pl-[13px] pr-[12px] pb-[27px] shadow-[0px_4px_7px_rgba(0,0,0,0.25)] md:w-[302px] md:px-[10px] md:pt-[11px] [&_.product-card-v2]:!max-w-full [&_.product-card-v2]:!min-w-full [&_.product-card-v2]:!min-h-[370px] md:[&_.product-card-v2]:!min-h-[476px] overflow-hidden"
                        >
                          <ProductCard
                            index={index}
                            product={product}
                            isSlider
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {shouldShowSliderControls ? (
                    <div className="absolute -bottom-7 left-0 right-0 h-[4px] bg-[rgba(255,255,255,0.2)] rounded">
                      <div
                        className="absolute top-0 h-full rounded bg-[linear-gradient(90deg,rgba(255,255,255,0.95)_0%,rgba(255,255,255,0.30)_55%,rgba(255,255,255,0.1)_100%)]"
                        style={{
                          width: `${thumbWidthPercent}%`,
                          left: `${thumbOffsetPercent}%`,
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </InView>
  )
}
